import cn from 'classnames';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import React, { forwardRef, HTMLAttributes, ImgHTMLAttributes } from 'react';
import { dehydrate, QueryClient } from 'react-query';
import { prefetchProfileByUsernameQuery, useProfileByUsernameQuery } from '../../api/useProfileByUsernameQuery';
import {
  prefetchProfileRecipesByUsernameQuery,
  useProfileRecipesByUsernameQuery,
} from '../../api/useProfileRecipesByUsernameQuery';
import { Button } from '../../components/Button';
import { Footer } from '../../components/Footer';
import { Header } from '../../components/Header';
import { Icon } from '../../components/Icon';
import { RecipeCard } from '../../components/RecipeCard';

type Props = {
  readonly username: string;
};

export default function ShowProfilePage({ username }: Props) {
  const { data: profileData } = useProfileByUsernameQuery({ username });

  const {
    data: recipesData,
    hasNextPage: hasMoreRecipes,
    isFetchingNextPage: fetchingMoreRecipes,
    fetchNextPage: fetchMoreRecipes,
  } = useProfileRecipesByUsernameQuery({ username });
  const profile = profileData?.profile;

  const title = `Choosy - @${username}`;
  const description =
    'Choosy findet, was dir schmeckt. Lecker und ausgewogen durch die Woche. Individuell. Gesund. Abwechslungsreich.';

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {profile?.image_url ? <meta property="og:image" content={profile.image_url} /> : null}
      </Head>
      <Header />
      <main className="flex-grow mt-7.5 sm:mt-8.75 lg:mt-12 xl:mt-16">
        <Section>
          <div className="flex justify-center">
            <ProfileImage src={profile?.image_url ?? undefined} />
          </div>
          <h2 className="mt-8 flex flex-col items-center">
            <ProfileName>{profile?.profile_name}</ProfileName>
            {profile?.username ? <UserName>@{profile.username}</UserName> : null}
          </h2>
        </Section>
        <Section>
          <ul
            className={cn(
              'flex flex-col items-center',
              'md:flex-row md:justify-between',
              '-ml-4',
              'space-y-4 md:space-y-0',
            )}
          >
            {profile?.website_url ? (
              <li className="ml-4">
                <WebsiteLink href={profile.website_url} />
              </li>
            ) : null}
            {profile?.instagram_account ? (
              <li className="ml-4">
                <InstagramLink instagramAccount={profile.instagram_account} />
              </li>
            ) : null}
          </ul>
        </Section>
        <Section>
          <div className="mt-8 w-full grid grid-cols-1 md:grid-cols-2 gap-8 justify-items-center">
            {recipesData?.pages.map((page, i) => {
              return (
                <React.Fragment key={i}>
                  {page.recipes.map(recipe => (
                    <RecipeCard key={recipe.id} recipe={recipe} editable={false} />
                  ))}
                </React.Fragment>
              );
            })}
          </div>
          {hasMoreRecipes ? (
            <div className="flex justify-center mt-16">
              <Button type="button" onClick={() => fetchMoreRecipes()} disabled={fetchingMoreRecipes}>
                Mehr Rezepte laden
              </Button>
            </div>
          ) : null}
        </Section>
      </main>
      <Footer />
    </>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ params }) => {
  const username = params?.username;

  if (typeof username !== 'string') {
    return {
      notFound: true,
    };
  }

  const queryClient = new QueryClient();
  await prefetchProfileByUsernameQuery(queryClient, { username });
  await prefetchProfileRecipesByUsernameQuery(queryClient, { username });

  return {
    props: {
      username,
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};

const Section = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <section
    {...props}
    ref={ref}
    className={cn(
      'mx-auto',
      'max-w-160',
      'px-5 sm:px-6 md:px-20 lg:px-0',
      'pb-10 sm:pb-12 lg:pb-16 xl:pb-20',
      className,
    )}
  />
));

const ProfileImage = forwardRef<HTMLImageElement, ImgHTMLAttributes<HTMLImageElement>>(
  ({ className, alt, src = '/images/avatar.svg', ...props }, ref) => (
    <img
      {...props}
      ref={ref}
      src={src}
      alt={alt}
      className={cn('w-60 h-60', 'object-cover', 'rounded-full border', className)}
    />
  ),
);

const ProfileName = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    className={cn(
      'font-serif',
      'font-extrabold',
      'text-choosy-gray',
      'text-26px sm:text-30px lg:text-34px xl:text-40px',
      className,
    )}
  />
));

const UserName = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    className={cn(
      'font-serif',
      'font-semibold',
      'text-choosy-gray-light',
      'text-20px sm:text-22px lg:text-24px xl:text-26px',
      className,
    )}
  />
));

type WebsiteLinkProps = {
  readonly href: string;
  readonly className?: string;
};

function WebsiteLink({ href, className }: WebsiteLinkProps) {
  const hrefNormalized = normalizeWebsiteURL(href);
  const url = tryParseURL(hrefNormalized);

  return (
    <div className={cn('text-20px sm:text-22px lg:text-24px xl:text-26px', 'text-choosy-gray-dark ', className)}>
      <Icon id="link" />{' '}
      <a
        href={hrefNormalized}
        target="_blank"
        rel="noreferrer"
        className="text-choosy-green-darkest hover:text-choosy-green-lighter transition-colors duration-200"
      >
        {url?.host ?? href}
      </a>
    </div>
  );
}

function normalizeWebsiteURL(url: string) {
  return url.startsWith('http') ? url : `https://${url}`;
}

function tryParseURL(url: string) {
  try {
    return new URL(url);
  } catch (e) {
    return undefined;
  }
}

type InstagramLinkProps = {
  readonly instagramAccount: string;
  readonly className?: string;
};

function InstagramLink({ instagramAccount, className }: InstagramLinkProps) {
  return (
    <div className={cn('text-20px sm:text-22px lg:text-24px xl:text-26px', 'text-choosy-gray-dark', className)}>
      <Icon id="instagram" />{' '}
      <a
        href={`https://www.instagram.com/${instagramAccount}`}
        target="_blank"
        rel="noreferrer"
        className="text-choosy-green-darkest hover:text-choosy-green-lighter transition-colors duration-200"
      >
        {instagramAccount}
      </a>
    </div>
  );
}
